// Simplified mockups of the places where homeowners see a profile, shown in
// the claim / takedown visibility card. They depict other surfaces (our pages,
// Google, an AI chat, an e-mail, an ad), so they keep a light "screenshot"
// look in both themes.
import type { ReactNode } from 'react';

import { Avatar, Box, Stack, Typography } from '@mui/material';
import { BadgeCheck, Link2, Search, Sparkles } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

import type { CountryShowcase } from './claimShowcaseData';

export type ShowcaseEntity = {
  name: string;
  photo?: string | null;
  isYou?: boolean;
  rating?: string;
  sales?: number;
  // Agencies get a square logo, agents a round photo.
  square?: boolean;
};

const INK = '#0f1116';
const MUTED = '#5b6170';
const LINE = 'rgba(15,17,22,0.09)';
const BLUE = '#5278DF';
const BLUE_WASH = '#F1F5FD';

const initials = (name: string) =>
  name
    .split(' ')
    .map(word => word[0])
    .slice(0, 2)
    .join('');

const EntityAvatar = ({
  entity,
  size = 26,
}: {
  entity: ShowcaseEntity;
  size?: number;
}) => (
  <Avatar
    src={entity.photo ?? undefined}
    alt=""
    variant={entity.square === true ? 'rounded' : 'circular'}
    sx={{
      width: size,
      height: size,
      fontSize: size * 0.36,
      fontWeight: 700,
      bgcolor: '#e6ebf7',
      color: '#3454D1',
      flexShrink: 0,
    }}
  >
    {initials(entity.name)}
  </Avatar>
);

const YouBadge = () => {
  const { t } = useLocale();
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75,
        px: 0.75,
        py: '1px',
        borderRadius: 999,
        bgcolor: BLUE,
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        verticalAlign: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {t('You')}
    </Box>
  );
};

const EntityRow = ({
  entity,
  right,
}: {
  entity: ShowcaseEntity;
  right?: ReactNode;
}) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      alignItems: 'center',
      px: 1,
      py: 0.75,
      borderRadius: '6px',
      border: `1px solid ${entity.isYou === true ? BLUE : LINE}`,
      bgcolor: entity.isYou === true ? BLUE_WASH : '#fff',
    }}
  >
    <EntityAvatar entity={entity} />
    <Typography
      noWrap
      sx={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: INK }}
    >
      {entity.name}
      {entity.isYou === true && <YouBadge />}
    </Typography>
    {entity.isYou !== true && right != null && (
      <Typography noWrap sx={{ fontSize: 11, color: MUTED, flexShrink: 0 }}>
        {right}
      </Typography>
    )}
  </Stack>
);

const Frame = ({ url, children }: { url?: string; children: ReactNode }) => (
  <Box
    sx={{
      maxWidth: 440,
      bgcolor: '#fff',
      color: INK,
      border: `1px solid ${LINE}`,
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 8px 24px -14px rgba(0,0,0,0.25)',
    }}
  >
    {url != null && (
      <Typography
        noWrap
        sx={{
          px: 1.25,
          py: 0.5,
          fontSize: 10.5,
          color: MUTED,
          bgcolor: '#f1f3f6',
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        {url}
      </Typography>
    )}
    <Stack spacing={0.5} sx={{ p: 1.5 }}>
      {children}
    </Stack>
  </Box>
);

const Muted = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ fontSize: 11.5, color: MUTED, lineHeight: 1.45 }}>
    {children}
  </Typography>
);

const Heading = ({ children }: { children: ReactNode }) => (
  <Typography
    sx={{ fontSize: 14, fontWeight: 700, color: INK, lineHeight: 1.3 }}
  >
    {children}
  </Typography>
);

const Big = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ fontSize: 20, fontWeight: 700, color: INK }}>
    {children}
  </Typography>
);

const Label = ({ children }: { children: ReactNode }) => (
  <Typography
    sx={{
      pt: 1,
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: '#9aa0ab',
    }}
  >
    {children}
  </Typography>
);

type MockProps = {
  country: CountryShowcase;
  isAgency: boolean;
  you: ShowcaseEntity;
  others: ShowcaseEntity[];
};

export const ValuationMock = ({
  country,
  isAgency,
  you,
  others,
}: MockProps) => {
  const { t } = useLocale();
  return (
    <Frame url={`${country.domain}/…`}>
      <Muted>{country.address}</Muted>
      <Big>{country.homeValue}</Big>
      <Muted>
        {t('Estimated range: {{range}}', { range: country.valueRange })}
      </Muted>
      <Label>
        {isAgency
          ? t('Recommended agencies near this home')
          : t('Recommended agents near this home')}
      </Label>
      {[others[0], you, others[1]].map(entity => (
        <EntityRow
          key={entity.name}
          entity={entity}
          right={t('{{sales}} sales nearby', { sales: entity.sales ?? 0 })}
        />
      ))}
    </Frame>
  );
};

export const RankingsMock = ({ country, isAgency, you, others }: MockProps) => {
  const { t } = useLocale();
  return (
    <Frame url={`${country.domain}/…`}>
      <Heading>
        {isAgency
          ? t('Best real estate agencies in {{town}}', { town: country.town })
          : t('Best real estate agents in {{town}}', { town: country.town })}
      </Heading>
      <Box sx={{ pb: 0.5 }}>
        <Muted>{t('Sorted by sales over the last 24 months')}</Muted>
      </Box>
      {[others[0], others[1], you, others[2]].map((entity, index) => (
        <Stack
          key={entity.name}
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center' }}
        >
          <Typography
            sx={{ width: 22, fontSize: 12, fontWeight: 700, color: MUTED }}
          >
            #{index + 1}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <EntityRow
              entity={entity}
              right={`★ ${entity.rating ?? ''} · ${t('{{sales}} sales', {
                sales: entity.sales ?? 0,
              })}`}
            />
          </Box>
        </Stack>
      ))}
    </Frame>
  );
};

const BARS = [52, 58, 61, 66, 70, 74, 80, 86];

export const PricesMock = ({ country, you, others }: MockProps) => {
  const { t } = useLocale();
  return (
    <Frame url={`${country.domain}/…`}>
      <Heading>
        {t('Property prices in {{town}}', { town: country.town })}
      </Heading>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'baseline' }}>
        <Big>{country.pricePerSqm}</Big>
        <Muted>{t('per m² · apartments')}</Muted>
      </Stack>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: 'flex-end', height: 44, pt: 0.5 }}
      >
        {BARS.map((height, index) => (
          <Box
            key={height}
            sx={{
              flex: 1,
              height: `${height}%`,
              borderRadius: '3px 3px 0 0',
              bgcolor:
                index === BARS.length - 1 ? BLUE : 'rgba(82,120,223,0.25)',
            }}
          />
        ))}
      </Stack>
      <Label>{t('Active in this area')}</Label>
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.75 }}>
        {[you, others[0], others[1]].map(entity => (
          <Stack
            key={entity.name}
            direction="row"
            spacing={0.75}
            sx={{
              alignItems: 'center',
              pl: 0.375,
              pr: 1,
              py: 0.25,
              borderRadius: 999,
              border: `1px solid ${entity.isYou === true ? BLUE : LINE}`,
              bgcolor: entity.isYou === true ? BLUE_WASH : '#fff',
            }}
          >
            <EntityAvatar entity={entity} size={20} />
            <Typography
              sx={{ fontSize: 11, fontWeight: 600, color: INK }}
              noWrap
            >
              {entity.name}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Frame>
  );
};

const GoogleMark = () => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: 999,
      bgcolor: '#2563eb',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&::after': {
        content: '""',
        width: 7,
        height: 7,
        bgcolor: '#fff',
        borderRadius: '1px',
        transform: 'rotate(45deg)',
      },
    }}
  />
);

export const GoogleMock = ({ country, isAgency, you }: MockProps) => {
  const { t } = useLocale();
  return (
    <Frame>
      <Box sx={{ fontFamily: 'Arial, sans-serif' }}>
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            alignItems: 'center',
            px: 1.25,
            py: 0.625,
            mb: 1.25,
            borderRadius: 999,
            border: '1px solid #dfe1e5',
            color: '#9aa0a6',
          }}
        >
          <Search size={13} />
          <Typography noWrap sx={{ fontSize: 12, color: '#202124' }}>
            {isAgency
              ? t('best real estate agency {{town}}', { town: country.town })
              : t('best real estate agent {{town}}', { town: country.town })}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <GoogleMark />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, color: '#202124' }}>
              RealAdvisor
            </Typography>
            <Typography noWrap sx={{ fontSize: 11, color: '#4d5156' }}>
              https://{country.domain} › …
            </Typography>
          </Box>
        </Stack>
        <Typography sx={{ mt: 0.5, fontSize: 15, color: '#1a0dab' }}>
          {isAgency
            ? t('{{name}} – Real estate agency in {{town}} | RealAdvisor', {
                name: you.name,
                town: country.town,
              })
            : t('{{name}} – Real estate agent in {{town}} | RealAdvisor', {
                name: you.name,
                town: country.town,
              })}
        </Typography>
        <Typography sx={{ mt: 0.25, fontSize: 11.5, color: '#4d5156' }}>
          {t(
            'Sales over the last 24 months, verified reviews and contact details. Compare with the other professionals in {{town}}.',
            { town: country.town },
          )}
        </Typography>
      </Box>
    </Frame>
  );
};

export const AiMock = ({ country, isAgency, you, others }: MockProps) => {
  const { t } = useLocale();
  return (
    <Frame>
      <Stack spacing={1.25}>
        <Box
          sx={{
            alignSelf: 'flex-end',
            maxWidth: '85%',
            px: 1.375,
            py: 0.875,
            bgcolor: '#f1f3f6',
            borderRadius: '14px 14px 4px 14px',
          }}
        >
          <Typography sx={{ fontSize: 12, color: INK }}>
            {isAgency
              ? t('Which real estate agency should I choose in {{town}}?', {
                  town: country.town,
                })
              : t('Who is a good real estate agent in {{town}}?', {
                  town: country.town,
                })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Box
            sx={{
              width: 22,
              height: 22,
              flexShrink: 0,
              borderRadius: 999,
              bgcolor: BLUE_WASH,
              color: BLUE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sparkles size={12} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: INK, lineHeight: 1.5 }}>
              {t(
                'Based on recent sales and verified reviews, good options in {{town}} include {{first}} and {{second}}.',
                {
                  town: country.town,
                  first: others[0]?.name ?? '',
                  second: you.name,
                },
              )}
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                mt: 0.75,
                px: 1,
                py: '1px',
                borderRadius: 999,
                border: `1px solid ${LINE}`,
                color: MUTED,
              }}
            >
              <Link2 size={11} />
              <Typography sx={{ fontSize: 10.5 }}>{country.domain}</Typography>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Frame>
  );
};

export const InboxMock = ({ country, isAgency, you, others }: MockProps) => {
  const { t } = useLocale();
  return (
    <Frame>
      <Muted>{t('From RealAdvisor · Your home’s value this month')}</Muted>
      <Box sx={{ pb: 0.5 }}>
        <Heading>
          {t('Your apartment is worth about {{value}}', {
            value: country.homeValue,
          })}
        </Heading>
      </Box>
      <Label>
        {isAgency
          ? t('Recommended agencies near you')
          : t('Recommended agents near you')}
      </Label>
      {[others[0], you].map(entity => (
        <EntityRow
          key={entity.name}
          entity={entity}
          right={`★ ${entity.rating ?? ''} · ${t('{{sales}} sales', {
            sales: entity.sales ?? 0,
          })}`}
        />
      ))}
    </Frame>
  );
};

// The ad as it runs, in the country's language: the same layout as our live
// "verified reviews in <town>" creatives.
export const ExampleAd = ({ country }: { country: CountryShowcase }) => (
  <Box
    sx={{
      maxWidth: 340,
      mx: 'auto',
      p: 1.75,
      borderRadius: '6px',
      color: INK,
      background: 'linear-gradient(160deg, #eef2ff, #f8fafc)',
    }}
  >
    <Box
      component="img"
      src="/realadvisor-logo.svg"
      alt="RealAdvisor"
      sx={{ height: 15, display: 'block' }}
    />
    <Typography
      sx={{
        my: 1.25,
        fontSize: 18,
        fontWeight: 800,
        lineHeight: 1.15,
        letterSpacing: '-0.01em',
      }}
    >
      {country.ad.headlineStart}
      <Box component="span" sx={{ color: '#2563eb' }}>
        {country.ad.headlinePlace}
      </Box>
      {country.ad.headlineEnd}
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 0.875,
      }}
    >
      {country.agents.map(agent => (
        <Box
          key={agent.name}
          sx={{
            bgcolor: '#fff',
            borderRadius: '6px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <Box
            component="img"
            src={agent.photo}
            alt=""
            loading="lazy"
            sx={{
              display: 'block',
              width: '100%',
              aspectRatio: '1',
              objectFit: 'cover',
            }}
          />
          <Typography
            noWrap
            sx={{ px: 0.625, pt: 0.5, fontSize: 9, fontWeight: 700 }}
          >
            {agent.name}
          </Typography>
          <Typography
            noWrap
            sx={{ px: 0.625, pb: 0.625, fontSize: 8, color: MUTED }}
          >
            <Box component="span" sx={{ color: '#f59e0b' }}>
              ★
            </Box>{' '}
            {agent.adStats}
          </Typography>
        </Box>
      ))}
    </Box>
    <Typography sx={{ mt: 1.5, fontSize: 11, fontWeight: 700 }}>
      {country.ad.footer}
      <Box component="span" sx={{ color: '#f59e0b' }}>
        ★
      </Box>
    </Typography>
    <Typography
      sx={{ mt: 0.5, fontSize: 11, fontWeight: 600, color: '#2563eb' }}
    >
      {country.ad.link}
    </Typography>
  </Box>
);

// A sold property as public profile pages show it: photo with SOLD and month
// badges, type and rooms, town, who sold it, verified badge.
export const SoldPropertyCard = ({
  country,
  agentName,
  agencyName,
}: {
  country: CountryShowcase;
  agentName: string;
  agencyName: string | null;
}) => {
  const { t, intlLocale } = useLocale();
  const month = new Intl.DateTimeFormat(intlLocale, {
    month: 'short',
    year: 'numeric',
  }).format(new Date(country.sold.date));
  const link = { color: '#2563eb', fontWeight: 600 };
  return (
    <Stack
      direction="row"
      spacing={1.25}
      sx={{
        p: 1,
        bgcolor: '#fff',
        color: INK,
        border: `1px solid ${LINE}`,
        borderRadius: '8px',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: 104, sm: 140 },
          minHeight: 100,
          flexShrink: 0,
          borderRadius: '6px',
          backgroundImage: `url(${country.sold.photo})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 5,
            left: 5,
            px: 0.625,
            borderRadius: '4px',
            bgcolor: '#dbeafe',
            color: '#1d4ed8',
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          {t('SOLD')}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: 5,
            right: 5,
            px: 0.625,
            borderRadius: '4px',
            bgcolor: '#fff',
            boxShadow: 'inset 0 0 0 1px #e5e7eb',
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          {month}
        </Box>
      </Box>
      <Stack spacing={0.625} sx={{ minWidth: 0, py: 0.25, fontSize: 12 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {t('Apartment • {{rooms}} rooms', { rooms: country.sold.rooms })}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, color: '#4b5563' }}>
          {country.town}
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#4b5563' }}>
          {t('Sold by')}{' '}
          <Box component="span" sx={link}>
            {agentName}
          </Box>
        </Typography>
        {agencyName != null && (
          <Typography sx={{ fontSize: 12, color: '#4b5563' }}>
            {t('from')}{' '}
            <Box component="span" sx={link}>
              {agencyName}
            </Box>
          </Typography>
        )}
        <Stack
          direction="row"
          spacing={0.375}
          sx={{ alignItems: 'center', color: '#15803d' }}
        >
          <BadgeCheck size={13} />
          <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
            {t('Verified')}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
